import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Table, Button, OverlayTrigger, Tooltip, Badge } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaUndo } from "react-icons/fa";
import APIServicenew from "../../utils/APIGeneralService";
import { UserContext } from "../../context/UserContextProvider";
import { ToastContainer, toast } from "react-toastify";
import DeleteModal from "../../components/common/DeleteModel";
import FAQFilter from "./FAQFilter";
import useMenuPermissions from "../../components/CustomHook/custompermission";
import { formateDatemdy } from "../../utils/Helper";
import Paginator from "../../components/common/Pagination";

const ManageFAQ = () => {
  const permissionscontext = useMenuPermissions("/admin/faq");
  const [faqList, setFaqList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [count, setCount] = useState(0);
  const [selectedFAQ, setSelectedFAQ] = useState(null);
  const [showDt, setShowDt] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const navigate = useNavigate();
  const limit = 10;

  const APIServices = new APIServicenew(process.env.REACT_APP_OTHER_API_URL);
  const authToken = process.env.REACT_APP_authToken;
  const { accessToken, logout } = useContext(UserContext);

  const headers = {
    "Content-Type": "application/json",
    "auth-token": authToken,
    "authorization": accessToken
  };

  useEffect(() => {
    fetchFAQs();
  }, [currentPage, filters, searchTerm]);

  const fetchFAQs = async () => {
    setLoading(true);
    try {
      const params = {
        skip: (currentPage - 1) * limit,
        limit: limit,
      };

      if (searchTerm && searchTerm.trim() !== "") {
        params.search = searchTerm.trim();
      }
     
      if (filters.status && filters.status !== "") {
        params.status = filters.status;
      }

      const queryString = new URLSearchParams(params).toString();

      const response = await APIServices.get(
        `/admin/faq/faq_list?${queryString}`,
        headers
      );

      if (response?.data?.status === true) {
        const faqsData = response?.data?.data?.faqs || [];
        setFaqList(faqsData);
        const total = response?.data?.data?.total || 0;
        setCount(total);
        setTotalCount(Math.ceil(total / limit) || 0);
      } else if (response?.status === 401) {
        logout();
        navigate("/");
      } else {
        setFaqList([]);
        setCount(0);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching FAQs:", error);
      showToast("Error fetching FAQs", "error");
      setFaqList([]);
      setCount(0);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (faq) => {
    const newStatus = faq.status === "Active" ? "Inactive" : "Active";
    try {
      const payload = { id: faq.id, status: newStatus };
      const res = await APIServices.post(
        `/admin/faq/faq_status_update`,
        payload,
        headers
      );

      if (res?.data?.status === true) {
        showToast(res?.data?.message || "Status updated successfully", "success");
        fetchFAQs();
      } else {
        showToast(res?.data?.message || "Something went wrong.", "error");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      showToast("Error updating status", "error");
    }
  };

  const handleDeleteFAQ = async () => {
    try {
      if (selectedFAQ !== null) {
        const payload = { id: selectedFAQ };
        const res = await APIServices.post(
          `/admin/faq/faq_delete`,
          payload,
          headers
        );

        if (res?.data?.status === true) {
          showToast(res?.data?.message || "FAQ deleted successfully", "success");
          
          if (faqList.length === 1 && currentPage > 1) {
            setCurrentPage(currentPage - 1);
          } else {
            fetchFAQs();
          }
          
          setSelectedFAQ(null);
        } else {
          showToast(res?.data?.message || "Something went wrong.", "error");
        }
      }
      setShowDt(false);
    } catch (error) {
      console.error("Error deleting FAQ:", error);
      showToast("Error deleting FAQ", "error");
      setShowDt(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearch = () => {
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleApplyFilter = (filterData) => {
    setFilters(filterData);
    setCurrentPage(1);
    setShowFilter(false);
  };

  const handleClearFilter = () => {
    setFilters({});
    setCurrentPage(1);
    setShowFilter(false);
  };

  const showToast = (message, type) => {
    toast(message, {
      position: "top-right",
      autoClose: 2000,
      type,
      hideProgressBar: false,
      closeOnClick: true,
    });
  };

  const getSerialNumber = (index) => {
    return (currentPage - 1) * limit + index + 1;
  };

  const truncateText = (text, limit = 100) => {
    if (!text) return "";
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  const renderWithTooltip = (text, limit = 100, placement = "top") => {
    if (!text) return "";

    if (text.length > limit) {
      return (
        <OverlayTrigger
          placement={placement}
          overlay={
            <Tooltip id={`tooltip-${Math.random()}`}>
              <div style={{ maxWidth: '400px', wordWrap: 'break-word' }}>
                {text}
              </div>
            </Tooltip>
          }
        >
          <span className="cursor-pointer" style={{ cursor: 'help' }}>
            {truncateText(text, limit)}
          </span>
        </OverlayTrigger>
      );
    }
    return text;
  };

  const hasActiveFilters = Object.keys(filters).some(key => filters[key] && filters[key] !== "");

  return (
    <>
      <div className="momList_inner todo_list_inner faq_list_inner">
        <div className="mom_header d-flex align-items-center justify-content-between mb-3">
          <h1 className="font_blue_18 font-600">
            {`FAQ Management (${count})`}
          </h1>

          <ul className="d-flex align-items-center mom_filter">
            <li
              className="d-flex align-items-center cursor-pointer"
              onClick={() => setShowFilter(true)}
            >
              Filter By{" "}
              <img
                src="/images/Sort.svg"
                className="img-fluid ms-2"
                alt="Sort"
              />
            </li>
            <li>
              {permissionscontext.add == 1 && (<button
                className="btn red_button d-flex align-items-center"
                onClick={() => navigate("/admin/add-faq")}
              >
                <FaPlus className="me-1" />
                Add FAQ
              </button>)}
            </li>
          </ul>
        </div>

        {/* Search Section */}
        <div className="search_section mb-3">
          <div className="row">
            <div className="col-md-4">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by question or answer..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                <Button
                  variant="primary"
                  onClick={handleSearch}
                  className="d-flex align-items-center"
                >
                  <FaSearch className="me-1" /> Search
                </Button>
                {searchTerm && (
                  <Button
                    variant="outline-secondary"
                    onClick={handleClearSearch}
                    className="d-flex align-items-center"
                  >
                    <FaUndo /> Clear
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="active_filters mb-3">
            <div className="d-flex align-items-center flex-wrap gap-2">
              <span className="text-muted">Active Filters:</span>
              {filters.status && (
                <Badge bg="info" className="px-3 py-2">
                  Status: {filters.status}
                  <button
                    className="btn btn-sm text-white ms-2"
                    onClick={() => {
                      setFilters({});
                      setCurrentPage(1);
                    }}
                    style={{ background: 'none', border: 'none', padding: 0 }}
                  >
                    ×
                  </button>
                </Badge>
              )}
            </div>
          </div>
        )}

        <div className="todoList_table">
          <div className="table-responsive">
            <Table>
              <thead className="table-dark">
                <tr>
                  <th style={{ width: '5%' }}>Sr.No</th>
                  <th style={{ width: '25%' }}>Question </th>
                  <th style={{ width: '40%' }}>Answer </th>
                  <th style={{ width: '10%' }}>Status</th>
                  <th style={{ width: '10%' }}>Created </th>
                  <th style={{ width: '10%' }}>Manage</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : faqList.length > 0 ? (
                  faqList.map((faq, index) => (
                    <tr key={faq.id}>
                      <td>{getSerialNumber(index)}</td>
                      <td>
                        <div className="faq-question font-weight-bold">
                          {renderWithTooltip(faq.question, 100, "top")}
                        </div>
                      </td>
                      <td>
                        <div className="faq-answer text-muted">
                          {renderWithTooltip(faq.answer, 120, "bottom")}
                        </div>
                      </td>
                      <td>
                        <Form className="Switch_toggle">
                          <input
                            type="checkbox"
                            id={`switch-${faq.id}`}
                            checked={faq.status === "Active"}
                            onChange={() => handleStatusChange(faq)}
                            disabled={!permissionscontext.edit}
                          />
                          <label
                            className="position-relative"
                            htmlFor={`switch-${faq.id}`}
                          >
                            Toggle
                          </label>
                        </Form>
                      </td>
                      <td>{formateDatemdy(faq.created_at)}</td>
                      <td>
                        <ul className="d-flex align-items-center list-unstyled mb-0">
                          <li className="me-2">
                            <button
                              className="btn btn-sm btn-outline-success py-1"
                              onClick={() => {
                                if (permissionscontext.edit) {
                                  navigate(`/admin/update-faq/${faq.id}`, {
                                    state: { row: faq },
                                  });
                                }
                              }}
                              disabled={!permissionscontext.edit}
                              style={{
                                opacity: permissionscontext.edit ? 1 : 0.5,
                                cursor: permissionscontext.edit ? "pointer" : "not-allowed",
                              }}
                            >
                              <FaEdit />
                            </button>
                          </li>
                          <li>
                            <button
                              className="btn btn-sm btn-outline-danger py-1"
                              onClick={() => {
                                if (permissionscontext.delete) {
                                  setSelectedFAQ(faq.id);
                                  setShowDt(true);
                                }
                              }}
                              disabled={!permissionscontext.delete}
                              style={{
                                opacity: permissionscontext.delete ? 1 : 0.5,
                                cursor: permissionscontext.delete ? "pointer" : "not-allowed",
                              }}
                            >
                              <FaTrash />
                            </button>
                          </li>
                        </ul>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      <div className="text-muted">
                        <h5>No FAQs found</h5>
                        <p>
                          {hasActiveFilters || searchTerm 
                            ? "Try clearing filters or search terms" 
                            : "Click on 'Add FAQ' to create your first FAQ"}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>

        {/* Pagination - Exactly like reference code */}
        <div className="pagination_box mt-4">
          {count > limit && (
            <Paginator
              currentPage={currentPage}
              totalPages={totalCount}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>

      {/* Filter Offcanvas */}
      {showFilter && (
        <FAQFilter
          appliedFilters={filters}
          show={showFilter}
          onHide={() => setShowFilter(false)}
          onApplyFilter={handleApplyFilter}
          onClear={handleClearFilter}
        />
      )}

      {/* Delete Modal */}
      <DeleteModal
        showdt={showDt}
        handleCloseDt={() => setShowDt(false)}
        handledelete={handleDeleteFAQ}
      />

      <ToastContainer />
    </>
  );
};

export default ManageFAQ;